import { useState, React, useMemo } from 'react';
import { Col, Row, Modal, ModalBody, Spinner } from 'reactstrap';
import { ChevronRight, ChevronLeft, Send } from 'react-feather';
import { validarEmail } from '../../functions/documentType';
import BuscarAlumnos from '../../components/envioMails/BuscarAlumnos';
import RedactarMail from '../../components/envioMails/RedactarMail';
import envioMailService from '../../services/http/envioEmailService';
import ModalClickable from '../../layouts/modals/ModalClickable';
import colors from '../../theme/colors';

const EnvioMails = () => {
  const [pageCount, setPageCount] = useState(0);
  const [alumnos, setAlumnos] = useState([]);
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [selectedEmails, setSelectedEmails] = useState({});
  const [isSent, setIsSent] = useState(false);
  const [filters, setFilters] = useState({
    empresaId: 'CEDESCA',
    idFormationCenter: [],
    group: [],
  });
  const [emailGroup, setEmailGroup] = useState([]);
  const [emailResult, setEmailResult] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalMsg, setModalMsg] = useState('');

  const alumnosFiltrados = useMemo(
    () => alumnos.filter((alumno) => Object.keys(selectedEmails).includes(String(alumno.CODIGO))),
    [selectedEmails, alumnos],
  );

  const emailsDestinatarios = useMemo(
    () =>
      Object.values(selectedEmails)
        .flat()
        .filter((e) => !!e)
        .map((e) => e.trim()),
    [selectedEmails],
  );

  const handleOnClick = () => {
    if (pageCount === 1) {
      setPageCount(0);
    } else {
      setPageCount(1);
    }
  };

  const handleSend = async () => {
    const validEmails = emailsDestinatarios.filter((e) => validarEmail(e));
    const ccodclis = Object.keys(selectedEmails);
    const values = {
      email: 'no-reply@cedetecgroup.com',
      emails: validEmails,
      contenido: cuerpo,
      ccodclis,
      asunto,
      alumnosSelecteds: alumnosFiltrados,
    };

    setModalLoading(true);
    setModalMsg('');
    try {
      setIsSent(false);
      const response = await envioMailService.enviarCorreoMasivo(values, {
        empresaId: filters.empresaId,
      });
      setEmailResult(response);
      setIsSent(true);
      setModalMsg('OK');
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData?.correosOkey) {
        setEmailResult(responseData);
        setIsSent(true);
        setModalMsg('OK');
      } else {
        setIsSent(false);
        setModalMsg('Error al enviar el correo. Contacte con el administrador.');
      }
    } finally {
      setModalLoading(false);
    }
  };

  const resultMessage = useMemo(() => {
    const numberCorreosOkey = emailResult?.correosOkey?.length ?? 0;
    const correosFailed = emailResult?.correosFailed ?? [];
    const numberCorreosFailed = correosFailed.length;

    return (
      <Col>
        <Row className="m-1 justify-content-center">Petición de envío realizada.</Row>
        <Row className="m-1 text-start">
          El mail se ha enviado correctamente a {numberCorreosOkey} correo
          {numberCorreosOkey === 1 ? '' : 's'}.
        </Row>
        <Row className="m-1 text-start">
          {numberCorreosFailed > 0
            ? `Hay ${numberCorreosFailed} correo${
                numberCorreosFailed > 1 ? 's' : ''
              } con error de envío.`
            : 'No ha habido ningún correo con error de envío.'}
        </Row>
        {emailResult?.isConfirmationEmailOkey && (
          <Row className="m-1 text-start">
            Para mayor información, mira el correo de confirmación de mails que se te ha enviado.
          </Row>
        )}
      </Col>
    );
  }, [emailResult]);

  return (
    <>
      <Modal isOpen={modalLoading || !!modalMsg} centered>
        <ModalBody className="text-center p-4">
          {modalLoading ? (
            <>
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3">Enviando correos...</p>
            </>
          ) : modalMsg === 'OK' ? (
            <p className="mt-3">OK</p>
          ) : modalMsg ? (
            <p className="mt-3">{modalMsg}</p>
          ) : null}
        </ModalBody>
      </Modal>
      {isSent && <ModalClickable color="primary" header={resultMessage} url="/alumnos" />}
      <Col className="mt-3" md="12">
        {pageCount === 0 ? (
          <BuscarAlumnos
            setPageCount={setPageCount}
            alumnos={alumnos}
            setAlumnos={setAlumnos}
            setSelectedEmails={setSelectedEmails}
            selectedEmails={selectedEmails}
            filters={filters}
            setFilters={setFilters}
            emailGroup={emailGroup}
            setEmailGroup={setEmailGroup}
          />
        ) : (
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px',
            border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)',
            overflow: 'visible',
          }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
            }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                Redactar email
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={handleOnClick}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px',
                    fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
                    border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <ChevronLeft size={13} />
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={cuerpo.length === 0 || asunto.length === 0}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
                    fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
                    backgroundColor: colors.primary, color: '#fff',
                    cursor: (cuerpo.length === 0 || asunto.length === 0) ? 'not-allowed' : 'pointer',
                    opacity: (cuerpo.length === 0 || asunto.length === 0) ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  <Send size={13} />
                  Enviar
                </button>
              </div>
            </div>
            {/* Contenido */}
            <div style={{ padding: '16px' }}>
              <RedactarMail
                setPageCount={setPageCount}
                setCuerpo={setCuerpo}
                setAsunto={setAsunto}
                asunto={asunto}
                cuerpo={cuerpo}
                emailsDestinatarios={emailsDestinatarios}
              />
            </div>
          </div>
        )}
        {alumnos.length > 0 && pageCount === 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
            {(Object.keys(selectedEmails).length === 0 || !emailGroup || emailGroup.length === 0) && (
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {Object.keys(selectedEmails).length === 0 && (!emailGroup || emailGroup.length === 0)
                  ? 'Selecciona alumnos y un grupo email'
                  : Object.keys(selectedEmails).length === 0
                  ? 'Selecciona al menos un alumno'
                  : 'Selecciona un grupo email'}
              </span>
            )}
            <button
              type="button"
              onClick={handleOnClick}
              disabled={Object.keys(selectedEmails).length === 0 || !emailGroup || emailGroup.length === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
                fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px', border: 'none',
                backgroundColor: colors.primary, color: '#fff',
                cursor: (Object.keys(selectedEmails).length === 0 || !emailGroup || emailGroup.length === 0) ? 'not-allowed' : 'pointer',
                opacity: (Object.keys(selectedEmails).length === 0 || !emailGroup || emailGroup.length === 0) ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              Siguiente
              <ChevronRight size={13} />
            </button>
          </div>
        )}
      </Col>
    </>
  );
};
export default EnvioMails;
